import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Modal, Radio, Select, Spin } from 'antd';
import PropTypes from 'prop-types';
import styles from './postalForm.module.scss';
import {
  DEFAULT_POSTAL_FORM_DELIVERY_WINDOW,
  getDefaultPostalFormPayer,
  POSTAL_FORM_TYPES,
  postalFormDeliveryWindowOptions,
  postalFormPayerOptions,
  postalFormTypeOptions,
} from '../constants/postalFormConstants';
import {
  buildPostalFormDocument,
  buildPostalFormRecipient,
  buildPostalFormSender,
} from '../utils/postalFormDocument';
import printHtmlDocument from '../utils/printPostalForm';
import { fetchSenderOptions } from '../../senders/api/sendersApi';

const recipientFieldLabels = [
  ['Ime i prezime primaoca pošiljke', 'fullName'],
  ['Ulica', 'street'],
  ['Poštanski br./Grad', 'postalCodeAndCity'],
  ['Kontakt', 'phone'],
];

// Polja primaoca za rucni unos - isti nazivi i redosled kao redovi na obrascu.
const manualRecipientFields = [
  { key: 'fullName', label: 'Ime i Prezime' },
  { key: 'street', label: 'Ulica' },
  { key: 'postalCodeAndCity', label: 'Poštanski br./Grad' },
  { key: 'phone', label: 'Kontakt' },
];

const EMPTY_MANUAL_RECIPIENT = {
  fullName: '',
  street: '',
  postalCodeAndCity: '',
  phone: '',
};

// manualRecipient: obrazac se ne otvara iz pacijenta (stranica Posiljaoci), pa se
// primalac ne povlaci sa kartona nego se upisuje u modalu, a napomene nema.
const PostalFormModal = ({
  isOpen,
  onClose,
  user,
  loading = false,
  error = '',
  manualRecipient = false,
}) => {
  const [type, setType] = useState(null);
  const [deliveryWindow, setDeliveryWindow] = useState(
    DEFAULT_POSTAL_FORM_DELIVERY_WINDOW
  );
  const [payer, setPayer] = useState(null);
  const [otkupninaValue, setOtkupninaValue] = useState('');
  const [printing, setPrinting] = useState(false);

  const [senderOptions, setSenderOptions] = useState([]);
  const [senderLoading, setSenderLoading] = useState(false);
  const [selectedSenderId, setSelectedSenderId] = useState(null);
  const [recipientDraft, setRecipientDraft] = useState(EMPTY_MANUAL_RECIPIENT);

  useEffect(() => {
    if (!isOpen) return;
    setType(null);
    setDeliveryWindow(DEFAULT_POSTAL_FORM_DELIVERY_WINDOW);
    setPayer(null);
    setOtkupninaValue('');
    setPrinting(false);
    setSelectedSenderId(null);
    setRecipientDraft(EMPTY_MANUAL_RECIPIENT);
  }, [isOpen, user?._id]);

  const handleRecipientFieldChange = (key, value) =>
    setRecipientDraft((prev) => ({ ...prev, [key]: value }));

  // Lista posiljalaca za padajuci meni (GET /senders/options - kompletna,
  // nepaginirana, sa svim poljima za popunu).
  useEffect(() => {
    if (!isOpen) return undefined;

    let active = true;
    setSenderLoading(true);

    (async () => {
      const { data, ok } = await fetchSenderOptions();
      if (!active) return;
      setSenderOptions(ok && Array.isArray(data) ? data : []);
      setSenderLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [isOpen]);

  // Za OTKUPNINU je primalac unapred oznacen, za ostale obrasce nacin placanja
  // mora da se izabere rucno.
  const handleTypeChange = (value) => {
    setType(value);
    setPayer(getDefaultPostalFormPayer(value));
    if (value !== POSTAL_FORM_TYPES.SA_OTKUPNINOM) setOtkupninaValue('');
  };

  const isRedemption = type === POSTAL_FORM_TYPES.SA_OTKUPNINOM;

  const recipient = useMemo(
    () =>
      manualRecipient
        ? Object.fromEntries(
            Object.entries(recipientDraft).map(([key, value]) => [
              key,
              value.trim(),
            ])
          )
        : buildPostalFormRecipient(user),
    [manualRecipient, recipientDraft, user]
  );

  const senderSelectOptions = useMemo(
    () =>
      senderOptions.map((option) => ({
        value: option._id,
        label: [option.fullName, option.city].filter(Boolean).join(' — '),
      })),
    [senderOptions]
  );

  const selectedSender = useMemo(
    () =>
      senderOptions.find((option) => option._id === selectedSenderId) || null,
    [senderOptions, selectedSenderId]
  );

  // Kad nista nije izabrano, buildPostalFormSender vraca podrazumevanog
  // (hardkodovanog) posiljaoca, pa obrazac ostaje popunjen kao i ranije.
  const sender = useMemo(
    () => buildPostalFormSender(selectedSender),
    [selectedSender]
  );

  const hasNoSenders = !senderLoading && senderOptions.length === 0;

  // Upozorenje o praznim poljima na kartonu ima smisla samo kad se primalac
  // povlaci iz pacijenta; kod rucnog unosa korisnik vidi prazna polja pred sobom.
  const missingRecipientFields = useMemo(
    () =>
      manualRecipient
        ? []
        : recipientFieldLabels
            .filter(([, key]) => !recipient[key])
            .map(([label]) => label),
    [manualRecipient, recipient]
  );

  const documentHtml = useMemo(
    () =>
      buildPostalFormDocument({
        type,
        deliveryWindow,
        payer,
        recipient,
        sender,
        otkupninaValue,
        // Obrazac sa rucnim unosom nema "Napomenu o posiljci".
        shipmentNote: manualRecipient ? '' : user?.shipmentNote,
      }),
    [
      type,
      deliveryWindow,
      payer,
      recipient,
      sender,
      otkupninaValue,
      manualRecipient,
      user,
    ]
  );

  const canPrint = Boolean(type && payer);

  const handlePrint = async () => {
    if (!canPrint) return;
    setPrinting(true);
    await printHtmlDocument(documentHtml);
    setPrinting(false);
  };

  const showForm = !loading && !error;

  return (
    <Modal
      className="postal_form_modal"
      title="Obrazac za poštu"
      open={isOpen}
      onCancel={onClose}
      width={860}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Odustani
        </Button>,
        <Button
          key="print"
          type="primary"
          onClick={handlePrint}
          disabled={!showForm || !canPrint}
          loading={printing}
        >
          Odštampaj obrazac za poštu
        </Button>,
      ]}
    >
      {loading && (
        <div className={styles.loading}>
          <Spin />
        </div>
      )}

      {!!error && !loading && <Alert title={error} type="error" showIcon />}

      {showForm && (
        <>
          <div className={styles.options}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Pošiljalac</span>
              <Select
                value={selectedSenderId}
                onChange={setSelectedSenderId}
                options={senderSelectOptions}
                loading={senderLoading}
                disabled={hasNoSenders}
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Izaberite pošiljaoca"
              />
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Vrsta obrasca</span>
              <Select
                value={type}
                onChange={handleTypeChange}
                options={postalFormTypeOptions}
                placeholder="Izaberite vrstu obrasca"
              />
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Danas za sutra</span>
              <Radio.Group
                value={deliveryWindow}
                onChange={(event) => setDeliveryWindow(event.target.value)}
                options={postalFormDeliveryWindowOptions}
              />
            </div>

            <div className={styles.field}>
              <span className={styles.fieldLabel}>Način plaćanja</span>
              <Radio.Group
                value={payer}
                onChange={(event) => setPayer(event.target.value)}
                options={postalFormPayerOptions}
              />
            </div>

            {isRedemption && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Otkupnina</span>
                <Input
                  value={otkupninaValue}
                  onChange={(event) => setOtkupninaValue(event.target.value)}
                  placeholder="Unesite iznos otkupnine"
                />
              </div>
            )}
          </div>

          {manualRecipient && (
            <>
              <div className={styles.sectionLabel}>Primalac</div>
              <div className={styles.recipientFields}>
                {manualRecipientFields.map(({ key, label }) => (
                  <div key={key} className={styles.field}>
                    <span className={styles.fieldLabel}>{label}</span>
                    <Input
                      value={recipientDraft[key]}
                      onChange={(event) =>
                        handleRecipientFieldChange(key, event.target.value)
                      }
                      placeholder={label}
                      allowClear
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          {hasNoSenders && (
            <Alert
              title="Nema pošiljaoca — dodajte ga na stranici Pošiljaoci."
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
            />
          )}

          {missingRecipientFields.length > 0 && (
            <Alert
              title={`Podaci o primaocu koji nedostaju na kartonu pacijenta: ${missingRecipientFields.join(', ')}. Ta polja ostaju prazna na obrascu.`}
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
            />
          )}

          <div className={styles.previewLabel}>
            Pregled (A4 horizontalno, gornja polovina stranice)
          </div>
          <div className={styles.preview}>
            <iframe
              className={styles.previewFrame}
              title="Pregled obrasca za poštu"
              srcDoc={documentHtml}
            />
          </div>
        </>
      )}
    </Modal>
  );
};

PostalFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  manualRecipient: PropTypes.bool,
};

export default PostalFormModal;
